import React from "react";

import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { AlertProps } from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FieldType, IfacePropertySchema, InterfaceProperty } from "../../types/Interface";
import { WorkshopProduct } from "../../types/WorkshopProducts";
import { WorkshopProductCombination } from "../../types/WorkshopProductCombination";
import { WorkshopProductProperty, getWorkshopProductProperties, updateWorkshopproductProperty } from "../../types/WorkshopProductProperty";
import { PropertyInput } from "./PropertyInput";
import { useSnackbar } from '../../../contexts/SnackBarContext';
import SnackbarLocal from "../../SnackbarLocal";
import { groupBy } from "../../Utils";
import { FolderLabel } from '../../subs/FolderLabel';
import { PropertyJSONErrorModal } from "../../modals/PropertyJSONErrorModal";

export const GroupAccordion = ({ interfaceProperties, workshopProduct, workshopProductCombination, disabled,
    setPropertiesComplete, setPropertiesIncomplete, setPropertiesOptional, setPropertiesAllComplete }:
    {
        workshopProduct: WorkshopProduct, interfaceProperties: InterfaceProperty[], disabled?: boolean,
        workshopProductCombination: WorkshopProductCombination,
        setPropertiesComplete: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesIncomplete: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesOptional: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesAllComplete: React.Dispatch<React.SetStateAction<boolean>>,
       // workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [open, setOpen] = React.useState<boolean>(false);
    const [jsonErrorModalOpenState, setjsonErrorModalOpenState] = React.useState<boolean>(false);
    const [interfaceProperty, setInterfaceProperty] = React.useState<InterfaceProperty>();
    const [workshopProductProperty, setWorkshopProductProperty] = React.useState<WorkshopProductProperty>();
    const [message, setMessage] = React.useState<string>('');
    const [severity, setMessageSeverity] = React.useState<AlertProps['severity']>('info');
    const [workshopProductProperties, setWorkshopProductProperties] = React.useState<WorkshopProductProperty[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetch = async () => {
            if (workshopProduct.id === 0) return;   // if no node is selected, the requests will error
            console.log('selected node try: ', workshopProduct.id);
            try {
                const sharedAbortController = new AbortController();
                const workshopProductPropertyResponse = await getWorkshopProductProperties(workshopProduct.id, sharedAbortController);

                if (!workshopProductPropertyResponse.failed) {
                    let workshopProductPropertiesLocal = workshopProductPropertyResponse.result;
                    if (workshopProductPropertiesLocal !== undefined) {
                        workshopProductPropertiesLocal = workshopProductPropertiesLocal.filter(wpp => wpp.testCombinationId === workshopProductCombination.testCombinationId);
                    }
                    setWorkshopProductProperties(workshopProductPropertiesLocal);
                } else {
                    showUserFeedback(workshopProductPropertyResponse.errorText)
                }
            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        fetch();
    }, [workshopProduct, showUserFeedback, workshopProductCombination])

    React.useEffect(() => {
        if (workshopProductProperties.length === 0) return;
        try {
            let requiredProperties: number = 0;
            let optionalProperties: number = 0;
            interfaceProperties.forEach(ip => {
                if (!workshopProductProperties.find(wpp => wpp.interfacePropertyId === ip.id)) {
                    if (ip.fieldSchema === null || ip.fieldSchema.length === 0) {
                        if (ip.fieldType === FieldType.CheckBox) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                    else {
                        const schema: IfacePropertySchema = JSON.parse(ip.fieldSchema);
                        if (schema.isRequired) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                }
            });
            setPropertiesComplete(workshopProductProperties.length);
            setPropertiesIncomplete(requiredProperties);
            setPropertiesOptional(optionalProperties);
            setPropertiesAllComplete(requiredProperties === 0);
        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [workshopProductProperties, showUserFeedback, setPropertiesComplete, setPropertiesIncomplete, setPropertiesOptional, setPropertiesAllComplete, interfaceProperties])

    React.useEffect(() => {
        setjsonErrorModalOpenState(false);
        if (disabled) return;    // if the form is disabled, we don't want to show the json error modal even if there are json errors, since the user can't do anything about them
        if (workshopProductProperties.length === 0) return;
        var BreakException = {};        // react limitation doesn't allow to break out of a forEach loop, so we throw an exception and catch it immediately to break the loop when we find a json error
        try {
            workshopProductProperties.forEach(wpp => {
                if (wpp && wpp.jsonError && wpp.value !== '') {
                    const interfacePropertyLoc = interfaceProperties.find(ip => ip.id === wpp.interfacePropertyId);
                    if (interfacePropertyLoc) {     // interfaceProperties might still be loading this should re-trigger when they are loaded 
                        setInterfaceProperty(interfacePropertyLoc);
                        setWorkshopProductProperty(wpp);
                        setjsonErrorModalOpenState(true);
                        throw BreakException;
                    }
                }
            });
        } catch (error) {
            if (error !== BreakException) showUserFeedback(String(error));
        }
    }, [workshopProductProperties, showUserFeedback, interfaceProperties])

    const workshopProductPropertySubmitHandler = async (wpp: WorkshopProductProperty) => {
        if (workshopProduct.id === undefined) return;
        const index = workshopProductProperties.findIndex(item => item.interfacePropertyId === wpp.interfacePropertyId &&
            item.workshopProductId === wpp.workshopProductId && item.testCombinationId === wpp.testCombinationId);

        wpp.workshopProductId = workshopProduct.id;
        wpp.testCombinationId = workshopProductCombination.testCombinationId;
        wpp.value = wpp.value?.trim() ?? '';

        let response = await updateWorkshopproductProperty(wpp);

        if (response.failed) {
            setMessageSeverity('error');
            setMessage(response.errorText);
            setOpen(true);
            return;
        }
        wpp = response.result;

        if (index !== -1)
            setWorkshopProductProperties([...workshopProductProperties.slice(0, index), { ...wpp, workshopProductId: workshopProduct.id }, ...workshopProductProperties.slice(index + 1, workshopProductProperties.length)]);
        else
            setWorkshopProductProperties([...workshopProductProperties, { ...wpp, workshopProductId: workshopProduct.id }]);
        setMessageSeverity('info');
        setMessage("Product property support updated.");
        setOpen(true);
    }

    const ifacePropGroups: { [key: string]: InterfaceProperty[] } = groupBy(interfaceProperties, ip => ip.group);
    const wppGroups: { [key: string]: WorkshopProductProperty[] } =
        groupBy(workshopProductProperties ?? [], wpp => interfaceProperties.find(ip => ip.id === wpp.interfacePropertyId)?.group ?? 'undefined');
    const groupKeys = [...Object.keys(ifacePropGroups)];

    // some interfaces don't contain any groups
    return (interfaceProperties.length === 0) ? <></> : <>
        <PropertyJSONErrorModal
            openState={jsonErrorModalOpenState} setOpenState={setjsonErrorModalOpenState}
            interfaceProperty={interfaceProperty}
            workshopProduct={workshopProduct}
            workshopProductProperty={workshopProductProperty}
            workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler} />
        {groupKeys.map((key, index) => {
            return <SubGroupAccordion key={groupKeys[index]} interfaceProperties={ifacePropGroups[groupKeys[index]]} workshopProductProperties={wppGroups[groupKeys[index]] ?? []}
                groupName={groupKeys[index]} disabled={disabled} workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler}/> }
        )}
        <SnackbarLocal duration={3000} severity={severity} message={message} open={open} setOpen={setOpen} />
    </>
}

export const SubGroupAccordion = ({ interfaceProperties, workshopProductProperties, groupName, disabled, workshopProductPropertySubmitHandler }:
    {
        interfaceProperties: InterfaceProperty[], workshopProductProperties: WorkshopProductProperty[],
        groupName: string, disabled?: boolean,
        workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
        // workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [propertiesComplete, setPropertiesComplete] = React.useState<number>(0);
    const [propertiesIncomplete, setPropertiesIncomplete] = React.useState<number>(0);
    const [propertiesOptional, setPropertiesOptional] = React.useState<number>(0);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        try {
            let requiredProperties: number = 0;
            let optionalProperties: number = 0;
            interfaceProperties.forEach(ip => {
                if (!workshopProductProperties.find(wpp => wpp.interfacePropertyId === ip.id)) {
                    if (ip.fieldSchema === null || ip.fieldSchema.length === 0) {
                        if (ip.fieldType === FieldType.CheckBox) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                    else {
                        const schema: IfacePropertySchema = JSON.parse(ip.fieldSchema);
                        if (schema.isRequired) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                }
            });
            setPropertiesComplete(workshopProductProperties.length);
            setPropertiesIncomplete(requiredProperties);
            setPropertiesOptional(optionalProperties);
        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [workshopProductProperties, interfaceProperties, showUserFeedback])

    // some interfaces don't contain any groups
    return (interfaceProperties.length === 0) ? <></> : <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                <Typography>{groupName}</Typography>
                {<FolderLabel labelText={''} labelVariant='large' noicon={true}
                    passed={disabled ? 0 : propertiesComplete} info={disabled ? 0 : propertiesOptional} errors={disabled ? 0 : propertiesIncomplete} />}
            </Stack>
        </AccordionSummary>

        <AccordionDetails>
            <form>
                <fieldset disabled={disabled} className={'disabledForm'}>
                    <Grid container rowGap={10}>
                        {interfaceProperties.map(ifaceProperty => {
                            return <Grid key={ifaceProperty.id} size={{ xs: ifaceProperty.fieldType === FieldType.Table || ifaceProperty.fieldType === FieldType.MultiLineBox ? 12 : 4 }} columnGap={5}>
                                <PropertyInput workshopProductProperty={workshopProductProperties?.find(wpp => wpp.interfacePropertyId === ifaceProperty.id) as WorkshopProductProperty}
                                    interfaceProperty={ifaceProperty} disabled={disabled} workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler} />
                            </Grid>
                        })}
                    </Grid>
                </fieldset>
            </form>
        </AccordionDetails>
    </Accordion>
}